import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Users, Cloud, Mail, ExternalLink } from "lucide-react";
import { NAV } from "@/data/site";
import WorkingHoursIndicator from "@/components/WorkingHoursIndicator";
import { HandDrawnUnderline, HandDrawnCorner } from "@/components/HandDrawn";

const EXTERNAL_TOOLS = [
  { name: "CRM", href: "https://crm.pratikteknoloji.tr", icon: Users, label: "PratikCRM", color: "#007AFF" },
  { name: "Cloud", href: "https://pratik.cloud", icon: Cloud, label: "PratikCLOUD", color: "#34C759" },
  { name: "Mail", href: "https://mail.pratikteknoloji.tr", icon: Mail, label: "PratikMAIL", color: "#FF9500" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10" : "bg-[#0A0A0A]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 group">
          <img src="/logos/pratik-teknoloji-white.png" alt="Pratik Teknoloji" className="h-12 md:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/[^a-z]/gi, "-") || "home"}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `relative px-2.5 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && (
                    <>
                      <HandDrawnUnderline />
                      <HandDrawnCorner />
                    </>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* External tools (CRM / Cloud) */}
          <div className="hidden md:flex items-center gap-1.5">
            {EXTERNAL_TOOLS.map((t) => (
              <a
                key={t.name}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                title={t.label}
                data-testid={`ext-tool-${t.name.toLowerCase()}`}
                className="group h-9 w-9 inline-flex items-center justify-center rounded-full border transition-all hover:scale-110"
                style={{
                  borderColor: `${t.color}40`,
                  background: `${t.color}15`,
                  color: t.color,
                }}
                aria-label={t.label}
              >
                <t.icon size={16} strokeWidth={2} />
              </a>
            ))}
          </div>
          <div className="hidden md:block">
            <WorkingHoursIndicator dark />
          </div>
          <Link
            to="/iletisim"
            data-testid="header-cta-contact"
            className="hidden md:inline-flex items-center rounded-full bg-[#FF3B30] text-white text-sm font-semibold px-5 py-2.5 hover:bg-white hover:text-[#0A0A0A] transition-colors"
          >
            Teklif Al
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 text-white"
            aria-label="Menü"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0A0A0A] border-t border-white/10">
          <div className="px-5 py-4 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold ${
                    isActive ? "bg-[#FF3B30] text-white" : "text-white/80 hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {/* External tools in mobile menu */}
            <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/40 px-4 mb-1">Araçlar</div>
              {EXTERNAL_TOOLS.map((t) => (
                <a
                  key={t.name}
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-3 rounded-xl text-white/80 hover:bg-white/5"
                >
                  <span className="flex items-center gap-3 font-semibold">
                    <t.icon size={18} strokeWidth={1.8} />
                    {t.label}
                  </span>
                  <ExternalLink size={14} className="text-white/40" />
                </a>
              ))}
            </div>
            <div className="pt-2"><WorkingHoursIndicator dark /></div>
          </div>
        </div>
      )}
    </header>
  );
}
